import { useEffect, useState } from "react";
import { Truck, Star, Phone } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useDeliveryDashboard,
  useDeliveryPartnerId,
  useUpdateDeliveryProfileMutation,
} from "@/hooks/queries";
import {
  computeCompletionRate,
  profileUiToUpdateDto,
} from "@/lib/mappers/delivery-mappers";
import { getApiErrorMessage } from "@/services/httpClient";
import type { DeliveryProfileUi } from "@/types/delivery.ui";
import { useToast } from "@/hooks/use-toast";

const DeliveryProfilePage = () => {
  const { partnerId, profile, isLoading: partnerLoading } = useDeliveryPartnerId();
  const { data: stats, isLoading: statsLoading } = useDeliveryDashboard(partnerId);
  const updateMutation = useUpdateDeliveryProfileMutation(partnerId ?? 0);
  const { toast } = useToast();

  const [form, setForm] = useState<DeliveryProfileUi | null>(null);

  useEffect(() => {
    if (profile) setForm(profile);
  }, [profile]);

  const isLoading = partnerLoading || statsLoading;

  const handleSave = async () => {
    if (!form || !partnerId) return;
    try {
      await updateMutation.mutateAsync(profileUiToUpdateDto(form));
      toast({ title: "Profile updated successfully" });
    } catch (error) {
      toast({
        title: "Failed to update profile",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const updateField = <K extends keyof DeliveryProfileUi>(key: K, value: DeliveryProfileUi[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  if (!partnerId || !profile || !form || !stats) {
    return (
      <div>
        <h1 className="text-2xl font-heading font-bold tracking-tight lg:text-3xl">My Profile</h1>
        <p className="text-muted-foreground text-sm mt-2">No delivery partner profile linked to your account.</p>
      </div>
    );
  }

  const completionRate = computeCompletionRate(stats);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold tracking-tight lg:text-3xl">My Profile</h1>
        <p className="text-muted-foreground text-sm mt-1">Manage your delivery partner profile.</p>
      </div>

      <Card className="bg-gradient-accent text-accent-foreground">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-accent-foreground/20 backdrop-blur">
              <Truck className="h-10 w-10" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-heading font-bold">{form.name}</h2>
              <p className="text-accent-foreground/70 text-sm">{form.vehicleType} • {form.vehicleNumber || "—"}</p>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-current" />
                  <span className="font-bold">{form.rating.toFixed(1)}</span>
                </div>
                <span className="text-accent-foreground/60 text-sm">{form.totalDeliveries} deliveries</span>
                <Badge className="bg-accent-foreground/20 border-0">Since {form.joinedDate}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Performance</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Completion Rate</p>
              <p className="text-lg font-bold">{completionRate}%</p>
              <Progress value={completionRate} className="h-2 mt-2" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Rating</p>
              <p className="text-lg font-bold">⭐ {stats.rating.toFixed(1)}</p>
              <Progress value={stats.rating * 20} className="h-2 mt-2" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Today</p>
              <p className="text-lg font-bold">{stats.todayDeliveries} deliveries</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total Earned</p>
              <p className="text-lg font-bold">₹{stats.totalEarnings.toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Phone className="h-5 w-5 text-primary" /> Personal Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Full Name</Label><Input value={form.name} onChange={(e) => updateField("name", e.target.value)} /></div>
            <div className="space-y-2"><Label>Phone</Label><Input value={form.phone} onChange={(e) => updateField("phone", e.target.value)} /></div>
            <div className="space-y-2"><Label>Email</Label><Input value={form.email} onChange={(e) => updateField("email", e.target.value)} /></div>
            <div className="space-y-2"><Label>City</Label><Input value={form.city} onChange={(e) => updateField("city", e.target.value)} /></div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><Truck className="h-5 w-5 text-accent" /> Vehicle Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Vehicle Type</Label><Input value={form.vehicleType} onChange={(e) => updateField("vehicleType", e.target.value)} /></div>
            <div className="space-y-2"><Label>Vehicle Number</Label><Input value={form.vehicleNumber} onChange={(e) => updateField("vehicleNumber", e.target.value)} /></div>
            <div className="space-y-2">
              <Label>Commission Per Delivery</Label>
              <Input value={`₹${form.commissionPerDelivery}`} disabled />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2" disabled={updateMutation.isPending}>
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default DeliveryProfilePage;
